/**
 * Серверные правила записи локального матча (POST /api/users/me/local-runs и
 * .../:matchId/actions): что принимается от клиента и как дописывается.
 * Чистые функции — проверяются юнит-тестами, сам сервер только ходит в базу.
 *
 * Общее с записью обучения (проверка пачки, дописывание по счетчику, объем)
 * переиспользуется из tutorialRunServerRules — там же лежит и сам движок
 * записи. Здесь только то, что у локального матча свое: пределы, имя записи и
 * прогресс (раунд вместо шага главы).
 */
import {FINAL_ROUND} from '../../../configs/game';
import {
  checkIfPlayerBonusType,
  checkIfSelectablePlayerBonusType,
  getPlayerBonusConfig,
  PLAYER_BONUS_TYPES,
} from '../../../configs/playerBonus';
import {getLegacyPlayerBonusConfig, getPerCityOnlyPlayerBonusConfig} from '../../../configs/playerBonusLegacy';

const checkIfSameBonusConfig = (bonusConfig, expected) => {
  if (!expected || !bonusConfig || typeof bonusConfig !== 'object') {
    return false;
  }
  const keys = Object.keys(expected);
  return Object.keys(bonusConfig).length === keys.length && keys.every((key) => bonusConfig[key] === expected[key]);
};

/**
 * Числа бонуса игрока локальной партии от клиента (зафиксированы на старте, см.
 * prepareLocalMatchPlayers) — для подсказки значка бота в истории. Данные
 * недоверенные: принимаются, только если это числа его уровня — текущие или
 * прежние: «только за города» (PER_CITY_ONLY_PLAYER_BONUS_CONFIG — клиент мог
 * начать партию до обновления) и фиксированные до 29.10 (LEGACY_PLAYER_BONUS_CONFIG).
 * Иначе null: числа не пишутся, и страница матча покажет фиксированные — так и
 * есть у партий клиентов до 29.10, которые чисел не присылают.
 *
 * @returns {Object|null} копия чисел или null
 */
export const getVerifiedLocalBonusConfig = (bonus, bonusConfig) => {
  if (!bonus || bonus === PLAYER_BONUS_TYPES.NONE || !checkIfPlayerBonusType(bonus)) {
    return null;
  }
  const candidates = [
    checkIfSelectablePlayerBonusType(bonus) ? getPlayerBonusConfig(bonus) : null,
    getPerCityOnlyPlayerBonusConfig(bonus),
    getLegacyPlayerBonusConfig(bonus),
  ];
  return candidates.some((expected) => checkIfSameBonusConfig(bonusConfig, expected)) ? {...bonusConfig} : null;
};

export const LOCAL_RUN_LIMITS = Object.freeze({
  // Пачка за один запрос: клиент шлет раз в раунд, но раунд бывает длинным —
  // рекордер сам режет очередь на пачки по 200 действий
  MAX_BATCH_ACTIONS: 300,
  // Вся запись партии: полная локальная партия — сотни действий, здесь запас
  MAX_ACTIONS: 5000,
  // Объем записи: реальная партия — десятые доли мегабайта
  MAX_BYTES: 1024 * 1024,
  // Партий в сутки на аккаунт: считается КАЖДЫЙ старт, включая брошенные
  MAX_RUNS_PER_DAY: 50,
  // Состав партии: столько игроков помещается на поле
  MAX_PLAYERS: 6,
  MAX_PLAYER_FIELD_LENGTH: 60,
});

/**
 * Идентификатор записи локального матча. Выводится из самого прохождения,
 * поэтому повтор создания (потерянный ответ) попадает в ту же запись, а
 * сохранение итогов партии находит ее по тому же ключу
 */
export const buildLocalRunMatchId = (userId, runId) => `local_${userId}_${runId.toLowerCase()}`;

/**
 * Снимок состава партии от клиента: у локального матча нет лобби, и сервер
 * знает об игроках только это. Данные недоверенные — режем длины и число
 * игроков, места и очки проставит сохранение итогов
 *
 * @returns {Array|null} null — состав не годится
 */
export const buildLocalRunPlayers = (players) => {
  if (!Array.isArray(players) || players.length === 0 || players.length > LOCAL_RUN_LIMITS.MAX_PLAYERS) {
    return null;
  }
  const cut = (value) => (typeof value === 'string' ? value.slice(0, LOCAL_RUN_LIMITS.MAX_PLAYER_FIELD_LENGTH) : '');
  return players.map((player) => {
    const bonusConfig = getVerifiedLocalBonusConfig(player?.bonus, player?.bonusConfig);
    return {
      playerId: cut(player?.id),
      name: cut(player?.name),
      color: cut(player?.color),
      factionId: cut(player?.factionId),
      selectedSkin: cut(player?.selectedSkin),
      isBot: Boolean(player?.isBot),
      // Помечен ли бот значком (см. checkIfBotMarkShown): у партии горячего
      // кресла — да, у сюжетной миссии, которая тоже сохраняется локальным
      // матчем, — нет
      isLocalBot: Boolean(player?.isLocalBot),
      // Уровень бонуса: из него на другом клиенте собирается путь к картинке
      // значка бота (см. getBotMarkIconPath), поэтому сырую строку не пускаем
      bonus: checkIfPlayerBonusType(player?.bonus) ? player.bonus : '',
      // Числа бонуса — уже на старте: незаконченная и брошенная партия видна в
      // истории до итогов (см. getVerifiedLocalBonusConfig)
      ...(bonusConfig ? {bonusConfig} : {}),
      // Места и очки известны только в конце партии (см. сохранение итогов)
      place: 0,
      finalWinPoints: 0,
    };
  });
};

/**
 * Есть ли в партии живой игрок.
 *
 * Партия целиком из ботов (все тумблеры переведены в «Бот») в историю
 * попадает — в ней видно и брошенные такие партии, — но САМИХ ДЕЙСТВИЙ в ней
 * не пишется: разбирать там нечего, а места запись занимает как обычная.
 * Отсюда же следует, что кнопки просмотра записи у такого матча не будет —
 * сервер отдает hasRecording по наличию самой записи.
 *
 * Игрок без поля isBot считается живым: состав приходит от клиента, и в
 * сомнительном случае лучше записать лишнее, чем потерять партию человека.
 */
export const checkIfHumanPlayerPresent = (players) => (
  Array.isArray(players) && players.some((player) => !player?.isBot)
);

/**
 * Лимит раундов партии от клиента: в локальной игре его выбирает сам игрок
 *
 * @returns {number|null}
 */
export const buildLocalRunFinalRound = (finalRound) => (
  Number.isInteger(finalRound) && finalRound > 0 && finalRound <= FINAL_ROUND ? finalRound : null
);

/**
 * До какого раунда дошла запись — по прогрессу из пачки. Номер только растет
 * (догнавшая keepalive-пачка не должна откатить его назад) и не выходит за
 * лимит раундов партии
 *
 * @returns {number|null} null — прогресс не годится или он не дальше прежнего
 */
export const buildLocalRunRecordedRound = ({round}, {lastRecordedRound = 0, finalRound = null} = {}) => {
  if (!Number.isInteger(round) || round <= 0) {
    return null;
  }
  // Номер за пределами партии — не подрезаем, а отвергаем: подделанная пачка
  // иначе сразу выставила бы конец партии, а номер только растет, и настоящие
  // раунды после этого уже не сдвинули бы его (так же поступает и обучение с
  // номером шага — см. buildTutorialRunProgress)
  const limit = Number.isInteger(finalRound) && finalRound > 0 ? finalRound : FINAL_ROUND;
  if (round > limit) {
    return null;
  }
  return round > (lastRecordedRound || 0) ? round : null;
};
